import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, ChevronRight, ClipboardCopy, Radar } from 'lucide-react';
import type {
  MarketingOpportunityItem,
  MarketingOpportunityReport,
  MarketingOpportunityReportListItem,
} from '../../types';
import { subscribeMarketingOpportunityReportDocs } from '../../utils/marketingReportsService';
import {
  buildOutreachBrief,
  extractCardLocation,
  extractDetailFields,
  extractOutreachAction,
  extractOutreachFields,
  extractSkipReason,
  sortOpportunitiesForOutreach,
  urgencyClassName,
  urgencyLabel,
} from '../../utils/marketingOpportunityOutreach';
import {
  buildOpportunityReportOptions,
  combinedOpportunityReportId,
  pickPreferredOpportunityReportId,
  resolveSelectedOpportunityReport,
} from '../../utils/marketingOpportunityReports';
import { useAuth } from '../../utils/AuthContext';
import { fmtReportDateLabel, localReportDateKey } from '../../utils/marketingReportDates';
import MarketingReportDateBar from './MarketingReportDateBar';

function linkValue(fieldKey: string, value: string): React.ReactNode {
  if (fieldKey.includes('url') || fieldKey.includes('link') || fieldKey === 'website') {
    return (
      <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer">
        {value}
      </a>
    );
  }
  if (fieldKey === 'email') return <a href={`mailto:${value}`}>{value}</a>;
  return value;
}

function OpportunityCard({
  item,
  index,
  reportDate,
}: {
  item: MarketingOpportunityItem;
  index: number;
  reportDate: string;
}) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const action = extractOutreachAction(item);
  const outreachFields = extractOutreachFields(item);
  const detailFields = extractDetailFields(item);
  const location = extractCardLocation(item);
  const priority = urgencyClassName(item.urgency);
  const canExpand = Boolean(action || outreachFields.length || detailFields.length);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(buildOutreachBrief(item, index, reportDate));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <article className={`mh-radar-card mh-radar-card--${priority}`}>
      <div className="mh-radar-card-top">
        <span className={`mh-radar-dot mh-radar-dot--${priority}`} />
        <span className="mh-radar-priority">{urgencyLabel(item.urgency)}</span>
        {item.category && <span className="mh-radar-category">{item.category}</span>}
      </div>

      <h3 className="mh-radar-title">{item.title}</h3>

      {item.rationale && <p className="mh-radar-rationale">{item.rationale}</p>}

      <div className="mh-radar-card-bottom">
        {location && <span className="mh-radar-location">{location}</span>}
        <span className="mh-radar-spacer" />
        <button type="button" className="mh-radar-copy" onClick={handleCopy}>
          {copied ? <Check size={12} /> : <ClipboardCopy size={12} />}
          {copied ? 'Copied' : 'Copy brief'}
        </button>
      </div>

      {canExpand && (
        <button
          type="button"
          className={`mh-radar-expand${open ? ' open' : ''}`}
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
        >
          <ChevronRight size={12} />
          {open ? 'Hide details' : 'Details'}
        </button>
      )}

      {open && canExpand && (
        <div className="mh-radar-details">
          {action && <p className="mh-radar-action">{action}</p>}
          {outreachFields.length > 0 && (
            <dl className="mh-radar-fields">
              {outreachFields.map(field => (
                <div key={`${field.key}-${field.value}`} className="mh-radar-field">
                  <dt>{field.label}</dt>
                  <dd>{linkValue(field.key, field.value)}</dd>
                </div>
              ))}
            </dl>
          )}
          {detailFields.length > 0 && (
            <dl className="mh-radar-fields">
              {detailFields.map(field => (
                <div key={`${field.key}-${field.value}`} className="mh-radar-field">
                  <dt>{field.label}</dt>
                  <dd>{field.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </article>
  );
}

function LeftoverRow({ item }: { item: MarketingOpportunityItem }) {
  const [open, setOpen] = useState(false);
  const reason = extractSkipReason(item);
  const detailFields = extractDetailFields(item);
  const canExpand = detailFields.length > 0;

  return (
    <article className="mh-radar-row">
      <div className="mh-radar-row-main">
        <div className="mh-radar-row-copy">
          <h4 className="mh-radar-row-title">{item.title}</h4>
          {reason && <p className="mh-radar-row-reason">{reason}</p>}
        </div>
        {item.category && <span className="mh-radar-category">{item.category}</span>}
      </div>
      {canExpand && (
        <button
          type="button"
          className={`mh-radar-expand${open ? ' open' : ''}`}
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
        >
          <ChevronRight size={12} />
          {open ? 'Hide details' : 'Details'}
        </button>
      )}
      {open && canExpand && (
        <dl className="mh-radar-fields">
          {detailFields.map(field => (
            <div key={`${field.key}-${field.value}`} className="mh-radar-field">
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  );
}

const MarketingOpportunityRadarPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [listLoading, setListLoading] = useState(true);
  const [reports, setReports] = useState<MarketingOpportunityReport[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const todayKey = useMemo(() => localReportDateKey(), []);
  const listItems = useMemo<MarketingOpportunityReportListItem[]>(
    () =>
      reports.map(r => ({
        id: r.id ?? r.report_date,
        report_date: r.report_date,
        opportunity_count: r.opportunities.length,
        headline: r.summary?.slice(0, 120) ?? r.opportunities[0]?.title ?? '',
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      })),
    [reports],
  );
  const reportOptions = useMemo(
    () => buildOpportunityReportOptions(listItems, todayKey),
    [listItems, todayKey],
  );
  const report = useMemo(
    () => resolveSelectedOpportunityReport(selectedReportId, reports),
    [selectedReportId, reports],
  );
  const hasTodayReport = listItems.some(r => r.report_date === todayKey);
  const todayCombinedId = combinedOpportunityReportId(todayKey);
  const sortedOpportunities = useMemo(
    () => (report ? sortOpportunitiesForOutreach(report.opportunities) : []),
    [report],
  );
  const leftoverSignals = report?.otherSignals ?? [];

  useEffect(() => {
    if (authLoading || !user) return;
    setListLoading(true);
    setError(null);
    const unsub = subscribeMarketingOpportunityReportDocs(
      rows => {
        setReports(rows);
        setListLoading(false);
        setError(null);
        const items = rows.map(r => ({
          id: r.id ?? r.report_date,
          report_date: r.report_date,
          opportunity_count: r.opportunities.length,
          headline: r.opportunities[0]?.title ?? '',
          createdAt: r.createdAt,
          updatedAt: r.updatedAt,
        }));
        setSelectedReportId(prev => pickPreferredOpportunityReportId(items, prev, true));
      },
      err => {
        setError(err.message);
        setListLoading(false);
      },
    );
    return unsub;
  }, [authLoading, user]);

  if (authLoading || listLoading) {
    return (
      <div className="marketing-hub-loading">
        <div className="marketing-hub-loading-bar" />
      </div>
    );
  }

  if (!reports.length) {
    return (
      <div className="marketing-hub-layout marketing-hub-layout--subpage">
        <div className="marketing-hub-main">
          <div className="marketing-hub-toolbar">
            <div className="marketing-hub-toolbar-left">
              <h2 className="marketing-hub-title">Opportunity Radar</h2>
            </div>
          </div>
          {error && (
            <div className="mh-requests-error" role="alert">
              {error}
            </div>
          )}
          <div className="mh-radar-empty">
            <Radar size={28} strokeWidth={1.5} />
            <h3>{error ? 'Could not load radar reports' : 'No radar reports yet'}</h3>
            <p>
              Opportunity radar reports land in <code>marketingOpportunityReports</code> after the Mac
              mini syncs <code>Data/Briefings/Marketing/Radar/YYYY-MM-DD.json</code>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="marketing-hub-layout marketing-hub-layout--subpage">
      <div className="marketing-hub-main">
        <div className="marketing-hub-toolbar">
          <div className="marketing-hub-toolbar-left">
            <h2 className="marketing-hub-title">Opportunity Radar</h2>
            <div className="marketing-hub-stats">
              <span className="mh-stat mh-stat-published">
                {sortedOpportunities.length} to pursue
              </span>
              {leftoverSignals.length > 0 && (
                <span className="mh-stat">{leftoverSignals.length} reviewed</span>
              )}
            </div>
          </div>
          <div className="marketing-hub-toolbar-right">
            <MarketingReportDateBar
              reports={reportOptions}
              selectedId={selectedReportId}
              onSelectId={setSelectedReportId}
              label="Report"
              compact
              todayKey={todayKey}
              todaySelectId={todayCombinedId}
              onJumpToToday={() => {
                if (hasTodayReport) {
                  const todayItems = listItems.filter(r => r.report_date === todayKey);
                  setSelectedReportId(
                    todayItems.length > 1 ? todayCombinedId : todayItems[0]?.id ?? todayCombinedId,
                  );
                }
              }}
            />
          </div>
        </div>

        <div className="mh-radar-body">
          {error && (
            <div className="mh-requests-error" role="alert">
              {error}
            </div>
          )}

          {!hasTodayReport && (
            <div className="mi-radar-today-notice" role="status">
              <AlertCircle size={16} aria-hidden />
              <div>
                <strong>No radar synced for today ({fmtReportDateLabel(todayKey)}).</strong>
                <span>
                  {' '}
                  Showing {report ? fmtReportDateLabel(report.report_date) : 'the latest available report'}.
                </span>
              </div>
            </div>
          )}

          {report?.summary && <p className="mh-radar-summary">{report.summary}</p>}

          {report && sortedOpportunities.length > 0 ? (
            <section className="mh-radar-section">
              <header className="mh-radar-section-head">
                <h3>To pursue</h3>
                <p>Sorted by priority. Copy a brief for your EA when you are ready to act.</p>
              </header>
              <div className="mh-radar-list">
                {sortedOpportunities.map((item, i) => (
                  <OpportunityCard
                    key={`${item.title}-${i}`}
                    item={item}
                    index={i}
                    reportDate={report.report_date}
                  />
                ))}
              </div>
            </section>
          ) : (
            <div className="mi-radar-today-notice" role="status">
              <AlertCircle size={16} aria-hidden />
              <div>
                <strong>
                  Report synced for {report ? fmtReportDateLabel(report.report_date) : 'this date'}, but no
                  opportunities were parsed.
                </strong>
              </div>
            </div>
          )}

          {leftoverSignals.length > 0 && (
            <section className="mh-radar-section">
              <header className="mh-radar-section-head">
                <h3>Excluded signals</h3>
                <p>Signals the radar reviewed that did not make today&apos;s outreach list.</p>
              </header>
              <div className="mh-radar-leftover">
                {leftoverSignals.map((item, i) => (
                  <LeftoverRow key={`${item.title}-${i}`} item={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingOpportunityRadarPage;
